import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import style from "../common.module.scss";
import { Driver, File, Order } from '../../../../services';
import UserProfileIcon from '../../../shared/icons/UserProfileIcon';
import TransportCoveredIconNew from '../../../shared/icons/TransportCoveredIconNew';
import moment from 'moment';
import DocumentIconNew from '../../../shared/icons/DocumentIconNew';
import generatePOA from '../../../../utils/generate_poa';
import DriverDocumentViewer from './DriverDocumentViewer';
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import ConfirmModal from '../../../shared/ConfirmModal/ConfirmModal';
import CustomButton from '../../../custom/custom-button/CustomButton';

const OrdersDriverInfo = ({ item, drivers = [], driverId = null, senderName, recipientName, bookingDate = '', listDocDriver = '', onUpdateData, fetchOrderData }) => {
    const { t } = useTranslation();
    const { user } = useSelector(state => state);
    const company = useSelector((state) => state.user.company);

    const [driversData, setDriversData] = useState({});
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [showModalDocumentViewer, setShowModalDocumentViewer] = useState(false);
    const [showRevokeModal, setShowRevokeModal] = useState(false);
    const [isLoading, setLoading] = useState(false);

    const normalizedDrivers = useMemo(() => {
        if (drivers.length > 0) {
            return [...drivers].sort((a, b) => a.position - b.position);
        }
        if (driverId) {
            return [{ driverId, vehicleId: item?.vehicleId, position: 1, id: 'legacy' }];
        }
        return [];
    }, [drivers, driverId, item?.vehicleId]);

    const hasMultipleDrivers = normalizedDrivers.length > 1;
    const activeDriver = normalizedDrivers[activeTabIndex] ?? null;
    const driverInfo = activeDriver ? driversData[activeDriver.driverId]?.driverInfo ?? null : null;
    const vehicleIdInfo = activeDriver ? driversData[activeDriver.driverId]?.vehicleInfo ?? null : null;

    const driversKey = normalizedDrivers.map(d => d.driverId).join(',');

    const fetchDriverDocs = async (driver) => {
        try {
            const response = await File.getDocList(driver.driverId);
            const filteredData = response.data.filter(doc => doc.type !== 8);
            const hasRequiredTypes = [4, 5, 7].some(type =>
                filteredData.some(doc => doc.type === type)
            );

            let docs = filteredData;
            if (!hasRequiredTypes && driver.vehicleId) {
                const vehicleResponse = await File.getDocListVehicle(driver.vehicleId);
                docs = [...filteredData, ...vehicleResponse.data];
            }

            setDriversData(prev => ({
                ...prev,
                [driver.driverId]: { ...prev[driver.driverId], docs }
            }));
        } catch (error) {
            console.log("Ошибка при получении документов водителя:", error);
        }
    };

    useEffect(() => {
        if (normalizedDrivers.length === 0) return;

        normalizedDrivers.forEach(driver => {
            Driver.getDriverInfo(driver.driverId)
                .then(response => {
                    if (response.status === 200) {
                        setDriversData(prev => ({
                            ...prev,
                            [driver.driverId]: {
                                ...prev[driver.driverId],
                                driverInfo: response.data || {},
                            }
                        }));
                    }
                })
                .catch(error => {
                    console.log("Ошибка при получении информации о водителе:", error);
                });

            if (driver.vehicleId) {
                Driver.getVehicleInfo(driver.vehicleId)
                    .then(response => {
                        if (response.status === 200) {
                            setDriversData(prev => ({
                                ...prev,
                                [driver.driverId]: {
                                    ...prev[driver.driverId],
                                    vehicleInfo: response.data,
                                }
                            }));
                        }
                    })
                    .catch(error => {
                        console.log("Ошибка при получении информации о vehicleInfo:", error);
                    });
            }

            fetchDriverDocs(driver);
        });
    }, [driversKey]);

    const handleRevokeExecutor = () => {
        setShowRevokeModal(true);
    };

    const confirmRevokeExecutor = async () => {
        setShowRevokeModal(false);
        setLoading(true);
        try {
            const status = (item?.companyId === company.id && item?.expeditorId === null)
                ? "published"
                : "assignedexecutor";
            await Order.updateExpeditorStatus(item.id, status, "");
            toast.success(t("orderInfo.orderDecision.revokeExecutorSuccess"));
            if (fetchOrderData) fetchOrderData();
            if (onUpdateData) onUpdateData();
        } catch (error) {
            console.error("Ошибка при отзыве исполнителя:", error);
            toast.error(t("orderInfo.genericError"));
        } finally {
            setLoading(false);
        }
    };

    const cancelRevokeExecutor = () => {
        setShowRevokeModal(false);
    };

    const handleGeneratePOA = () => {
        generatePOA({
            carModel: driverInfo.carModel,
            carNumber: driverInfo.carNumber,
            trailerNumber: driverInfo.trailerNumber,
            idNumber: driverInfo.idNumber,
            issuedDate: driverInfo.issuedDate,
            issuedBy: driverInfo.issuedBy,
        }, {
            surname: driverInfo.surname,
            name: driverInfo.name,
            patronymic: driverInfo.patronymic,
        }, senderName, recipientName, item?.number, item.shippingDate, item.unloadingDate, user.isNDS);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const options = { day: 'numeric', month: 'long', year: 'numeric' };
        return date.toLocaleDateString('ru-RU', options);
    };

    const getTabLabel = (driver) => {
        const info = driversData[driver.driverId]?.driverInfo;
        if (info?.surname) {
            return `Водитель: ${info.surname} ${info.name ? info.name.charAt(0).toUpperCase() + '.' : ''}`;
        }
        return `Водитель ${driver.position}`;
    };

    return (
        <div className={cn(style.cargo_wrapper)}>
            <div className={cn(style.executor_header, { [style.executor_header_no_border]: hasMultipleDrivers })}>
                <div className={cn(style.orders_title, style.textClamp2)}>
                    {t("orderInfo.ordersDriverInfo.executorTitle")} {item?.expeditorName && ` - ${item.expeditorName}`}
                </div>
                {item?.status === "confirmed" &&
                    (item?.expeditorId === company.id ||
                        (item?.companyId === company.id && item?.expeditorId === null)) && (
                        <CustomButton
                            text={t("orderInfo.orderDecision.revokeExecutorButton")}
                            onClick={handleRevokeExecutor}
                            isWhite={true}
                            isLoading={isLoading}
                            disabled={isLoading}
                            style={{ minWidth: "180px" }}
                        />
                    )}
            </div>

            {hasMultipleDrivers && (
                <div className={cn(style.driver_tabs, { [style.driver_tabs_two]: normalizedDrivers.length === 2 })}>
                    {normalizedDrivers.map((driver, index) => (
                        <button
                            key={driver.id || driver.driverId}
                            className={cn(style.driver_tab, {
                                [style.driver_tab_active]: activeTabIndex === index,
                            })}
                            onClick={() => setActiveTabIndex(index)}
                        >
                            {getTabLabel(driver)}
                        </button>
                    ))}
                </div>
            )}

            <div className={style.driver_content}>
                <div className={style.driver_details}>
                    <div className={style.driver_details_text}>{t('orderInfo.ordersDriverInfo.driverLabel')}</div>
                    <div className={style.driver_details_content}>
                        <div className={style.driver_details_icon}><UserProfileIcon /></div>
                        <div className={style.driver_details_info}>
                            <div className={style.driver_details_title}>
                                {driverInfo && driverInfo.surname ? `${driverInfo.name}.${driverInfo.surname.charAt(0).toUpperCase()}` : ''}
                            </div>
                            <div className={style.driver_details_text}>{t('orderInfo.ordersDriverInfo.iinLabel')} {driverInfo?.iin ? driverInfo.iin : '-'}</div>
                        </div>
                    </div>
                </div>
                <div style={{ display: "flex" }}>
                    <div className={style.driver_details}>
                        <div className={style.driver_details_text}>{t('orderInfo.ordersDriverInfo.transportLabel')}</div>
                        <div className={style.driver_details_content}>
                            <div className={style.driver_details_icon}><TransportCoveredIconNew /></div>
                            <div className={style.driver_details_info}>
                                <div className={style.driver_details_title}>{driverInfo?.carModel || vehicleIdInfo?.carModel}</div>
                                <div className={style.driver_details_text}>{driverInfo?.carNumber || vehicleIdInfo?.carNumber}</div>
                            </div>
                        </div>
                    </div>
                    <div className={style.driver_details}>
                        <div className={style.driver_details_text}>{t('orderInfo.ordersDriverInfo.trailerLabel')}</div>
                        <div className={style.driver_details_content}>
                            <div className={style.driver_details_info}>
                                <div className={style.driver_details_title}>{driverInfo?.trailerModel || vehicleIdInfo?.trailerModel}</div>
                                <div className={style.driver_details_text}>{driverInfo?.trailerNumber || vehicleIdInfo?.trailerNumber}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={style.driver_details}>
                    <div className={style.driver_details_text}>{t('orderInfo.ordersDriverInfo.documentsLabel')}</div>
                    <div className={style.driver_details_info}>
                        <div className={style.driver_details_title}>{t('orderInfo.ordersDriverInfo.idCardLabel')}</div>
                        <div className={style.driver_details_text}>
                            №{driverInfo?.idNumber}・{moment(driverInfo?.issuedDate).format("DD MMMM YYYY")}・{driverInfo?.issuedBy}
                        </div>
                    </div>
                </div>
                <div className={style.driver_details}>
                    <div className={style.driver_details_text}>{t("driversHead.phoneNumber")}</div>
                    <div className={style.driver_details_title}>
                        {driverInfo?.phoneNumber ? `+${driverInfo.phoneNumber}` : "-"}
                    </div>
                </div>
                <div className={style.driver_details}>
                    <div className={style.driver_details_text}>{t("orderInfo.ordersDriverInfo.driverDocsLabel")}</div>
                    <div className={style.driver_details_content}>
                        <div className={style.driver_details_docx}><DocumentIconNew type={2} /></div>
                        <div className={style.driver_details_info}>
                            <div className={style.driver_details_link} onClick={() => setShowModalDocumentViewer(true)}>{t('orderInfo.ordersDriverInfo.viewDocsLabel')}</div>
                            <div className={style.driver_details_text}>№{item.number} {t('orderInfo.ordersDriverInfo.docDateConnector')} {formatDate(bookingDate)}</div>
                        </div>
                    </div>
                </div>
            </div>

            {(activeDriver?.comment || activeDriver?.shipmentTransferDate) && (
              <div className={style.driver_details_route_bar}>
                <span className={style.driver_details_route_text}>
                  {activeDriver?.comment || "—"}
                </span>
                {activeDriver?.shipmentTransferDate && (
                  <span className={style.driver_details_route_date}>
                    {moment(activeDriver.shipmentTransferDate).format("DD.MM.YYYY")}
                  </span>
                )}
              </div>
            )}

            {showModalDocumentViewer && (
                <DriverDocumentViewer
                    setShowModal={setShowModalDocumentViewer}
                    documents={driversData[activeDriver?.driverId]?.docs ?? listDocDriver}
                    orderId={item?.id}
                />
            )}
            <ConfirmModal
                isOpen={showRevokeModal}
                title={t("orderInfo.orderDecision.revokeExecutorConfirmTitle")}
                message={t("orderInfo.orderDecision.revokeExecutorConfirmMessage")}
                confirmText={t("common.confirm")}
                cancelText={t("common.cancel")}
                onConfirm={confirmRevokeExecutor}
                onCancel={cancelRevokeExecutor}
                isLoading={isLoading}
            />
        </div>
    );
};

export default OrdersDriverInfo;
